import { msalInstance, loginRequest } from '@/config/msalConfig';
import { createLoginLogger } from '@/utils/loginLogger';

class MicrosoftAuthService {
  constructor() {
    this.msalInstance = msalInstance;
    this.initialized = false;
  }

  async initialize() {
    if (!this.initialized) {
      await this.msalInstance.initialize();
      this.initialized = true;
    }
  }

  async loginWithPopup(attemptId) {
    const logger = createLoginLogger('microsoft', attemptId);
    await this.initialize();
    logger.step('msal:initialized');

    try {
      logger.step('msal:loginPopup-begin');
      const response = await this.msalInstance.loginPopup(loginRequest);
      logger.step('msal:loginPopup-success', {
        account: response && response.account && response.account.username,
        hasAccessToken: !!(response && response.accessToken),
      });
      return response;
    } catch (error) {
      logger.error('msal:loginPopup', error);
      throw error;
    }
  }

  async loginWithRedirect() {
    await this.initialize();
    
    try {
      await this.msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Microsoft redirect login failed:', error);
      throw error;
    }
  }

  async getAccessToken(silent = true, attemptId) {
    const logger = createLoginLogger('microsoft', attemptId);
    await this.initialize();

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      logger.error('msal:getAccessToken-no-accounts', new Error('No accounts found. Please login first.'));
      throw new Error('No accounts found. Please login first.');
    }

    const request = {
      ...loginRequest,
      account: accounts[0],
    };

    try {
      if (silent) {
        logger.step('msal:acquireTokenSilent-begin');
        const response = await this.msalInstance.acquireTokenSilent(request);
        logger.step('msal:acquireTokenSilent-success');
        return response.accessToken;
      } else {
        logger.step('msal:acquireTokenPopup-begin');
        const response = await this.msalInstance.acquireTokenPopup(request);
        logger.step('msal:acquireTokenPopup-success');
        return response.accessToken;
      }
    } catch (error) {
      // On any silent acquisition failure (timeout, blocked 3rd-party cookies, interaction required),
      // fall back to interactive popup to guarantee a token.
      if (silent) {
        logger.step('msal:acquireTokenSilent-failed-fallback-to-popup', { code: error && (error.errorCode || error.code) });
        try {
          const response = await this.msalInstance.acquireTokenPopup(request);
          logger.step('msal:acquireTokenPopup-fallback-success');
          return response.accessToken;
        } catch (popupError) {
          // If popup also fails, rethrow the original error for better context
          logger.error('msal:acquireTokenPopup-fallback', popupError);
          throw error;
        }
      } else {
        logger.error('msal:acquireTokenPopup', error);
        throw error;
      }
    }
  }

  // The cached MSAL account for this sign-in email, or null. On a shared front-desk computer
  // another person's account can be cached too, so the session-health guard only ever re-auths
  // with the account that matches the Teamio user (src/plugins/sessionHealthGuard.js).
  findAccountByEmail(email) {
    const want = String(email || '').trim().toLowerCase();
    if (!want) return null;
    return this.getAllAccounts().find(
      (a) => String((a && a.username) || '').trim().toLowerCase() === want
    ) || null;
  }

  // SILENT ONLY: never opens a popup. The session-health guard runs with no user gesture, so a
  // popup would be blocked (getAccessToken(true) falls back to one). Resolves null on any failure.
  async acquireTokenSilentOnly(account) {
    if (!account) return null;
    try {
      await this.initialize();
      const response = await this.msalInstance.acquireTokenSilent({ ...loginRequest, account });
      return (response && response.accessToken) || null;
    } catch (_e) {
      return null;
    }
  }

  async logout() {
    await this.initialize();
    
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      await this.msalInstance.logoutPopup({
        account: accounts[0],
      });
    }
  }

  getAllAccounts() {
    return this.msalInstance.getAllAccounts();
  }

  isAuthenticated() {
    const accounts = this.getAllAccounts();
    return accounts.length > 0;
  }

  getCurrentAccount() {
    const accounts = this.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }
}

// Export singleton instance
export default new MicrosoftAuthService();
